import { useState } from 'react'
import { ArrowRight, X, AlertTriangle } from 'lucide-react'

interface TransitionNudgeProps {
  daysRemaining: number | null
  expiryDate: string | null
  declinedCount: number
  onAccept: () => void
  onDecline: () => void
}

export function TransitionNudge({
  daysRemaining,
  expiryDate,
  declinedCount,
  onAccept,
  onDecline,
}: TransitionNudgeProps) {
  const [submitting, setSubmitting] = useState(false)
  const isFinalDay = daysRemaining !== null && daysRemaining <= 1
  const formattedExpiry = expiryDate
    ? new Date(expiryDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null

  const handleAccept = async () => {
    setSubmitting(true)
    onAccept()
  }

  const handleDecline = () => {
    if (isFinalDay) return // Cannot dismiss final-day warning
    onDecline()
  }

  // Final-day warning - non-dismissible
  if (isFinalDay) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="mx-4 max-w-md rounded-lg border border-destructive/30 bg-card p-6 shadow-xl">
          <div className="flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" />
            <h2 className="text-lg font-semibold">Classic mode expires tomorrow</h2>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            The Command Centre layout will switch to the modern Ledger Core interface
            {formattedExpiry ? ` on ${formattedExpiry}` : ' tomorrow'}.
            This change is permanent and cannot be reversed.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Accept the guided transition now to extend your access and graduate tabs one at a time,
            giving you more time to adjust.
          </p>
          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              onClick={handleAccept}
              disabled={submitting}
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {submitting ? 'Accepting...' : 'Accept guided transition'}
              {!submitting && <ArrowRight className="ml-1.5 inline h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Regular weekly nudge - dismissible
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="mx-4 max-w-md rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-foreground">Ready to transition?</h2>
          <button
            onClick={handleDecline}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          The Command Centre layout {formattedExpiry
            ? `will be available until ${formattedExpiry}`
            : daysRemaining !== null
              ? `has ${daysRemaining} days remaining`
              : 'is temporary'
          }.
          {declinedCount === 0
            ? " We'll gradually move you to the modern interface, one section at a time."
            : " Accept the guided transition to extend your time and move at your own pace."}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Each week, the least-used tab will switch to the modern view. You keep
          full access to all your data - just with a better interface.
        </p>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            onClick={handleDecline}
            className="rounded border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
          >
            Not yet
          </button>
          <button
            onClick={handleAccept}
            disabled={submitting}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {submitting ? 'Accepting...' : 'Accept guided transition'}
            {!submitting && <ArrowRight className="ml-1.5 inline h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  )
}
